import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/* Star rating display — accepts float score (1.0-5.0), renders filled/partial/empty stars */
function StarRating({ score, size = 16 }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const fill = Math.min(1, Math.max(0, score - (i - 1)));
    if (fill >= 1) {
      stars.push(
        <span key={i} style={{ color: colors.goldMid, fontSize: size }}>&#9733;</span>
      );
    } else if (fill > 0) {
      stars.push(
        <span key={i} style={{ position: 'relative', display: 'inline-block', fontSize: size }}>
          <span style={{ color: colors.greyLight }}>&#9733;</span>
          <span style={{
            position: 'absolute', left: 0, top: 0,
            overflow: 'hidden', width: `${fill * 100}%`,
            color: colors.goldMid,
          }}>&#9733;</span>
        </span>
      );
    } else {
      stars.push(
        <span key={i} style={{ color: colors.greyLight, fontSize: size }}>&#9733;</span>
      );
    }
  }
  return <span style={{ whiteSpace: 'nowrap' }}>{stars}</span>;
}

const PLATFORMS = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: '💼',
    description: 'Professional posts, 800-1300 characters. Hook-first, paragraph-based.',
    charMin: 800,
    charMax: 1300,
  },
  {
    id: 'facebook_x',
    label: 'FB / X / Threads / Bluesky',
    icon: '🐦',
    description: 'Punchy posts, 280-500 characters. One idea, one punch.',
    charMin: 280,
    charMax: 500,
  },
  {
    id: 'pinterest',
    label: 'Pinterest',
    icon: '📌',
    description: 'Pin descriptions, visual content strategy.',
    disabled: true,
  },
];

function ShortFormPosts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [approvedPosts, setApprovedPosts] = useState([]);
  const [readiness, setReadiness] = useState(null);

  // Platform selection
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  // Topic generation
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicMode, setTopicMode] = useState('emerging');
  const [customTopicMode, setCustomTopicMode] = useState(false);
  const [customTopicInput, setCustomTopicInput] = useState('');

  // Draft generation
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [angleInput, setAngleInput] = useState('');

  // Edit & image
  const [editingText, setEditingText] = useState(false);
  const [editText, setEditText] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageError, setImageError] = useState('');

  // Modal
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, draftsRes, readinessRes] = await Promise.all([
        fetch('/api/content/profile', { headers: getAuthHeaders() }),
        fetch('/api/content/drafts?content_type=short_form', { headers: getAuthHeaders() }),
        fetch('/api/content/readiness', { headers: getAuthHeaders() }),
      ]);

      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile(data.profile);
      }

      if (draftsRes.ok) {
        const data = await draftsRes.json();
        const allDrafts = data.drafts || [];
        setDrafts(allDrafts.filter((d) => d.status === 'draft'));
        setApprovedPosts(allDrafts.filter((d) => d.status === 'approved'));
      }

      if (readinessRes.ok) {
        const data = await readinessRes.json();
        setReadiness(data);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateTopics = async (retryCount = 0) => {
    const maxRetries = 3;
    try {
      setGeneratingTopics(true);
      if (retryCount > 0) {
        setError(`Hang tight, experiencing a technical issue... (attempt ${retryCount + 1}/${maxRetries})`);
      }
      const res = await fetch('/api/content/generate-short-topics', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ count: 5, topic_mode: topicMode }),
      });

      if (res.ok) {
        const data = await res.json();
        const topics = data.topics || [];

        if (topics.length === 0 && retryCount < maxRetries - 1) {
          setGeneratingTopics(false);
          return generateTopics(retryCount + 1);
        }

        if (topics.length === 0) {
          setError('Something has gone wrong generating topics. The admin has been notified.');
          fetch('/api/admin/alert', {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              type: 'short_topic_generation_failed',
              message: 'Short form topic generation failed after 3 attempts',
            }),
          }).catch(() => {});
          return;
        }

        setError('');
        // Sort by score descending (highest rated first)
        topics.sort((a, b) => (b.score?.overall || 0) - (a.score?.overall || 0));
        setSuggestedTopics(topics);
      } else {
        setError('Failed to generate topics');
      }
    } catch (err) {
      setError('Failed to generate topics');
    } finally {
      setGeneratingTopics(false);
    }
  };

  const generateDraft = async (topic, angle = '') => {
    if (!selectedPlatform) return;

    try {
      setGeneratingDraft(true);
      const res = await fetch('/api/content/short-form-drafts', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          topic: topic,
          platform: selectedPlatform.id,
          angle: angle,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setCurrentDraft(data.draft);
        setEditText(data.draft.body);
        setShowGenerateModal(false);
        setShowPreviewModal(true);
        await loadData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to generate post');
      }
    } catch (err) {
      setError('Failed to generate post');
    } finally {
      setGeneratingDraft(false);
    }
  };

  const approveDraft = async (draftId) => {
    try {
      const res = await fetch(`/api/content/drafts/${draftId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data.stats);
        await loadData();
        setShowPreviewModal(false);
        setCurrentDraft(null);
      }
    } catch (err) {
      setError('Failed to approve draft');
    }
  };

  const saveDraftEdits = async () => {
    if (!currentDraft) return;
    try {
      const res = await fetch(`/api/content/drafts/${currentDraft.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ body: editText, title: editText.substring(0, 100) }),
      });
      if (res.ok) {
        setCurrentDraft({ ...currentDraft, body: editText, title: editText.substring(0, 100) });
        setEditingText(false);
        await loadData();
      }
    } catch (err) {
      setError('Failed to save edits');
    }
  };

  const rejectDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to reject this draft?')) return;
    try {
      const res = await fetch(`/api/content/drafts/${draftId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        await loadData();
        setShowPreviewModal(false);
        setCurrentDraft(null);
      }
    } catch (err) {
      setError('Failed to reject draft');
    }
  };

  const generateImage = async (draftId) => {
    try {
      setGeneratingImage(true);
      setImageError('');
      const res = await fetch(`/api/content/drafts/${draftId}/generate-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentDraft((prev) => ({ ...prev, featured_image_url: data.image_url }));
        await loadData();
      } else {
        const data = await res.json();
        setImageError(data.error || 'Failed to generate image. Please try again.');
      }
    } catch (err) {
      setImageError('Failed to generate image. Please try again.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const resetGenerateState = () => {
    setSuggestedTopics([]);
    setSelectedTopic(null);
    setCustomTopicMode(false);
    setCustomTopicInput('');
    setAngleInput('');
  };

  const getCharCountColor = (text, platform) => {
    if (!platform || !text) return colors.greyMid;
    const len = text.length;
    if (len > platform.charMax) return '#dc3545';
    if (len > platform.charMax * 0.9) return '#ffc107';
    if (len >= platform.charMin) return '#28a745';
    return colors.greyMid;
  };

  const getPlatformBadge = (platformTarget) => {
    if (platformTarget === 'linkedin') return { label: 'LinkedIn', color: '#0077B5' };
    return { label: 'FB/X', color: '#1DA1F2' };
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="mb-4">
        <h1>Short Form Posts</h1>
        <p className="text-muted">
          Social posts crafted in your voice for LinkedIn, X, Threads, and more.
        </p>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Readiness Warning */}
      {readiness && !readiness.is_ready && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading className="h6 mb-2">Complete Your Profile for Better Content</Alert.Heading>
          <ul className="mb-0 small">
            {readiness.missing.map((item, idx) => (
              <li key={idx}>
                <strong>{item.item}</strong>: {item.description}{' '}
                <Button variant="link" size="sm" className="p-0" onClick={() => navigate(item.link)}>
                  Complete now
                </Button>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Step 0: Platform Selection */}
      <div className="mb-4">
        <div className="small text-muted mb-2" style={{ fontWeight: 500 }}>Select Platform</div>
        <Row>
          {PLATFORMS.map((p) => (
            <Col key={p.id} md={4} className="mb-3">
              <Card
                style={{
                  cursor: p.disabled ? 'default' : 'pointer',
                  border: selectedPlatform?.id === p.id
                    ? `2px solid ${colors.goldMid}`
                    : `1px solid ${colors.greyLight}`,
                  opacity: p.disabled ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                }}
                className="shadow-sm h-100"
                onClick={() => {
                  if (!p.disabled) {
                    setSelectedPlatform(p);
                  }
                }}
                onMouseEnter={(e) => {
                  if (!p.disabled && selectedPlatform?.id !== p.id) {
                    e.currentTarget.style.borderColor = colors.goldMid;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!p.disabled && selectedPlatform?.id !== p.id) {
                    e.currentTarget.style.borderColor = colors.greyLight;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <Card.Body className="text-center p-3">
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{p.icon}</div>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{p.label}</div>
                  <div className="text-muted small">{p.description}</div>
                  {p.disabled && (
                    <Badge bg="secondary" className="mt-2">Coming Soon</Badge>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Topic Mode + Generate buttons (only after platform selected) */}
      {selectedPlatform && (
        <>
          <div className="mb-3">
            <div className="small text-muted mb-2" style={{ fontWeight: 500 }}>Topic Discovery Mode</div>
            <div className="d-flex flex-column gap-1">
              {[
                { key: 'popular', label: 'Popular', desc: 'Buzzy, high-engagement topics' },
                { key: 'emerging', label: 'Emerging Trends', desc: 'Early signals before they go mainstream' },
                { key: 'latest', label: 'Latest News', desc: 'Recent developments from the past 1-2 weeks' },
              ].map((mode) => (
                <Form.Check
                  key={mode.key}
                  type="radio"
                  id={`short-topic-mode-${mode.key}`}
                  name="shortTopicMode"
                  checked={topicMode === mode.key}
                  onChange={() => setTopicMode(mode.key)}
                  label={<span>{mode.label} <span className="text-muted small">- {mode.desc}</span></span>}
                />
              ))}
            </div>
          </div>

          <div className="mb-4 d-flex gap-2">
            <Button
              variant="outline-dark"
              onClick={() => {
                resetGenerateState();
                setCustomTopicMode(true);
                setShowGenerateModal(true);
              }}
            >
              + Enter a Topic
            </Button>
            <Button
              onClick={() => {
                resetGenerateState();
                setShowGenerateModal(true);
                generateTopics();
              }}
              style={{
                background: colors.goldMid,
                border: 'none',
                color: colors.greyDarkest,
              }}
            >
              + Generate Topics
            </Button>
          </div>
        </>
      )}

      {/* Pending Drafts */}
      {drafts.length > 0 && (
        <div className="mb-4">
          <h5>Pending Approval ({drafts.length})</h5>
          <Row>
            {drafts.map((draft) => {
              const badge = getPlatformBadge(draft.platform_target);
              return (
                <Col key={draft.id} md={6} lg={4} className="mb-3">
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Badge style={{ backgroundColor: badge.color }}>{badge.label}</Badge>
                        <span className="text-muted small">
                          {new Date(draft.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <Card.Text className="small" style={{
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {draft.body}
                      </Card.Text>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setCurrentDraft(draft);
                            setEditText(draft.body);
                            setEditingText(false);
                            setShowPreviewModal(true);
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => approveDraft(draft.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => rejectDraft(draft.id)}
                        >
                          Reject
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* Approved Posts */}
      {approvedPosts.length > 0 && (
        <div className="mb-4">
          <h5>Approved Posts ({approvedPosts.length})</h5>
          <Row>
            {approvedPosts.map((post) => {
              const badge = getPlatformBadge(post.platform_target);
              return (
                <Col key={post.id} md={6} lg={4} className="mb-3">
                  <Card className="h-100 shadow-sm" style={{ borderColor: colors.goldLight }}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Badge style={{ backgroundColor: badge.color }}>{badge.label}</Badge>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted small">
                            {new Date(post.approved_at || post.created_at).toLocaleDateString()}
                          </span>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Coming soon - GHL integration</Tooltip>}
                          >
                            <Button size="sm" variant="outline-secondary" disabled>
                              Post
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </div>
                      {post.featured_image_url && (
                        <img
                          src={post.featured_image_url}
                          alt="Post"
                          style={{
                            width: '100%',
                            maxHeight: '120px',
                            objectFit: 'contain',
                            borderRadius: '4px',
                            marginBottom: '0.5rem',
                          }}
                        />
                      )}
                      <Card.Text className="small" style={{
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {post.body}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* No content state */}
      {!selectedPlatform && drafts.length === 0 && approvedPosts.length === 0 && (
        <Card className="p-4 text-center text-muted">
          <p className="mb-0">Select a platform above to start generating social posts.</p>
        </Card>
      )}

      {/* ============================================================ */}
      {/* TOPIC GENERATION MODAL */}
      {/* ============================================================ */}
      <Modal
        show={showGenerateModal}
        onHide={() => {
          setShowGenerateModal(false);
          resetGenerateState();
        }}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: colors.greyDarkest, color: colors.goldLight }}
        >
          <Modal.Title>
            {customTopicMode ? 'Enter Your Topic' : 'Choose a Topic'}
            {selectedPlatform && (
              <Badge className="ms-2" style={{ backgroundColor: '#555', fontSize: '0.7rem' }}>
                {selectedPlatform.label}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customTopicMode ? (
            /* Custom topic entry */
            <div>
              <Form.Group className="mb-3">
                <Form.Label>What do you want to post about?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={customTopicInput}
                  onChange={(e) => setCustomTopicInput(e.target.value)}
                  placeholder="e.g., Why most businesses overcomplicate their AI strategy"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  Angle / Hook <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  value={angleInput}
                  onChange={(e) => setAngleInput(e.target.value)}
                  placeholder="e.g., The counterintuitive truth about simplicity"
                />
              </Form.Group>
              <Button
                onClick={() => generateDraft(customTopicInput, angleInput)}
                disabled={!customTopicInput.trim() || generatingDraft}
                style={{
                  background: `linear-gradient(90deg, #D03737, #A30202)`,
                  border: 'none',
                  color: '#fff',
                }}
              >
                {generatingDraft ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Generating...
                  </>
                ) : (
                  'Generate Post'
                )}
              </Button>
              <Button
                variant="link"
                className="ms-2"
                onClick={() => setCustomTopicMode(false)}
              >
                Use suggested topics instead
              </Button>
            </div>
          ) : (
            /* AI-generated topics */
            <div>
              {generatingTopics ? (
                <div className="text-center py-4">
                  <Spinner animation="border" style={{ color: colors.goldMid }} />
                  <p className="mt-2 text-muted">Searching for trending topics...</p>
                </div>
              ) : suggestedTopics.length > 0 ? (
                <>
                  {suggestedTopics.map((topic, idx) => {
                    const isTopPick = idx === 0 && topic.score;
                    const isWeak = topic.score && topic.score.overall < 4.0;
                    return (
                    <Card
                      key={idx}
                      className="mb-2 shadow-sm"
                      style={{
                        cursor: 'pointer',
                        border: selectedTopic === topic
                          ? `2px solid ${colors.goldMid}`
                          : `1px solid ${colors.greyLight}`,
                        borderLeft: isTopPick ? `4px solid ${colors.goldMid}` : undefined,
                        opacity: isWeak ? 0.65 : 1,
                      }}
                      onClick={() => {
                        setSelectedTopic(topic);
                        if (topic.angle) setAngleInput(topic.angle);
                      }}
                    >
                      <Card.Body className="py-2 px-3">
                        <div>
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <strong style={{ flex: 1, minWidth: 0 }}>{topic.topic}</strong>
                            {topic.score && (
                              <div className="text-end ms-3" style={{ flexShrink: 0 }}>
                                <StarRating score={topic.score.overall || 0} />
                                <span className="ms-1 small text-muted">{topic.score.overall}</span>
                              </div>
                            )}
                          </div>
                          {topic.angle && (
                            <div className="text-muted small">
                              Angle: {topic.angle}
                            </div>
                          )}
                          {topic.why_it_matters && (
                            <div className="text-muted small mt-1">
                              {topic.why_it_matters}
                            </div>
                          )}
                          {topic.score?.reason && (
                            <div className="small mt-1" style={{ color: colors.goldDark }}>
                              {topic.score.reason}
                            </div>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                    );
                  })}

                  {selectedTopic && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold">
                          Angle / Hook <span className="text-muted fw-normal">(optional - customize the take)</span>
                        </Form.Label>
                        <Form.Control
                          value={angleInput}
                          onChange={(e) => setAngleInput(e.target.value)}
                          placeholder="Add your own spin or leave the suggestion..."
                        />
                      </Form.Group>
                      <Button
                        onClick={() => generateDraft(selectedTopic.topic, angleInput)}
                        disabled={generatingDraft}
                        style={{
                          background: `linear-gradient(90deg, #D03737, #A30202)`,
                          border: 'none',
                          color: '#fff',
                        }}
                      >
                        {generatingDraft ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Generating...
                          </>
                        ) : (
                          'Generate Post'
                        )}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-muted">
                  <p>Click "Generate Topics" to get started.</p>
                </div>
              )}
              <div className="mt-3 text-center">
                <Button variant="link" size="sm" onClick={() => generateTopics()}>
                  Regenerate topics
                </Button>
                <span className="text-muted mx-1">|</span>
                <Button variant="link" size="sm" onClick={() => setCustomTopicMode(true)}>
                  Enter my own topic
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ============================================================ */}
      {/* PREVIEW / ACTIONS MODAL */}
      {/* ============================================================ */}
      <Modal
        show={showPreviewModal}
        onHide={() => {
          setShowPreviewModal(false);
          setEditingText(false);
          setImageError('');
        }}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: colors.greyDarkest, color: colors.goldLight }}
        >
          <Modal.Title className="d-flex align-items-center gap-2">
            Post Preview
            {currentDraft && (
              <Badge style={{
                backgroundColor: getPlatformBadge(currentDraft.platform_target).color,
                fontSize: '0.7rem',
              }}>
                {getPlatformBadge(currentDraft.platform_target).label}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentDraft && (
            <>
              {/* Character count indicator */}
              {(() => {
                const platform = PLATFORMS.find((p) => p.id === currentDraft.platform_target);
                const text = editingText ? editText : currentDraft.body;
                const charCount = text ? text.length : 0;
                const charColor = platform ? getCharCountColor(text, platform) : colors.greyMid;
                return (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small text-muted">
                      {platform ? `Target: ${platform.charMin}-${platform.charMax} chars` : ''}
                    </span>
                    <Badge style={{ backgroundColor: charColor, fontSize: '0.8rem' }}>
                      {charCount} characters
                    </Badge>
                  </div>
                );
              })()}

              {/* Post text */}
              {editingText ? (
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  style={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}
                />
              ) : (
                <Card
                  className="mb-3"
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: `1px solid ${colors.greyLight}`,
                  }}
                >
                  <Card.Body style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>
                    {currentDraft.body}
                  </Card.Body>
                </Card>
              )}

              {/* Image error */}
              {imageError && (
                <Alert variant="warning" className="mb-3" onClose={() => setImageError('')} dismissible>
                  {imageError}
                </Alert>
              )}

              {/* Image section */}
              {currentDraft.featured_image_url && (
                <div className="mb-3">
                  <img
                    src={currentDraft.featured_image_url}
                    alt="Post"
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                  />
                </div>
              )}

              {/* First comment suggestion */}
              {currentDraft.suggested_outcome && (
                <Card className="mb-3" style={{ border: `1px solid ${colors.goldLight}`, backgroundColor: '#fffdf5' }}>
                  <Card.Body className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <span className="small fw-bold" style={{ color: colors.goldDark }}>
                        Suggested First Comment
                      </span>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        style={{ fontSize: '0.75rem', padding: '1px 8px' }}
                        onClick={() => {
                          navigator.clipboard.writeText(currentDraft.suggested_outcome);
                          const btn = document.activeElement;
                          const orig = btn.textContent;
                          btn.textContent = 'Copied!';
                          setTimeout(() => { btn.textContent = orig; }, 1500);
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                      {currentDraft.suggested_outcome}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Action buttons */}
              <div className="d-flex gap-2 flex-wrap">
                {editingText ? (
                  <>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={saveDraftEdits}
                    >
                      Save Changes
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setEditText(currentDraft.body);
                        setEditingText(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline-dark"
                      onClick={() => {
                        setEditText(currentDraft.body);
                        setEditingText(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-dark"
                      onClick={() => generateDraft(
                        currentDraft.suggested_topic || currentDraft.title,
                        ''
                      )}
                      disabled={generatingDraft}
                    >
                      {generatingDraft ? (
                        <><Spinner size="sm" className="me-1" /> Regenerating...</>
                      ) : (
                        'Regenerate Text'
                      )}
                    </Button>
                    {!currentDraft.featured_image_url ? (
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => generateImage(currentDraft.id)}
                        disabled={generatingImage}
                      >
                        {generatingImage ? (
                          <><Spinner size="sm" className="me-1" /> Generating Image...</>
                        ) : (
                          'Generate Image'
                        )}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => generateImage(currentDraft.id)}
                        disabled={generatingImage}
                      >
                        {generatingImage ? (
                          <><Spinner size="sm" className="me-1" /> Regenerating...</>
                        ) : (
                          'Regenerate Image'
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => approveDraft(currentDraft.id)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => rejectDraft(currentDraft.id)}
                    >
                      Reject
                    </Button>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Coming soon - GHL integration</Tooltip>}
                    >
                      <span>
                        <Button size="sm" variant="outline-secondary" disabled>
                          Post
                        </Button>
                      </span>
                    </OverlayTrigger>
                  </>
                )}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default ShortFormPosts;
